// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { FormInstance } from 'antd';
import { convertToLocaleId, useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { CustomStatusMapType, UserOutput } from 'thehive-sdk';
import { Select } from 'antd';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { onOrganisationImageUpload } from '@Helpers/download';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { IconLabel } from '@Atoms/Info';

export interface AlertChangeStatusValuesForm {
    status: string;
    summary?: string;
    assignee?: string;
}

interface AlertChangeStatusFormProps {
    onFinish(values: AlertChangeStatusValuesForm): void;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    statusList: CustomStatusMapType;
    onCancel: () => void;
    assignableUsers?: UserOutput[];
    initialValues?: {
        status?: string;
        summary?: string;
    };
}
const { OptGroup } = Select;

export const AlertChangeStatusForm = ({
    onFinish,
    form,
    onChange,
    isValid,
    statusList,
    initialValues,
    assignableUsers,
    onCancel,
}: AlertChangeStatusFormProps): ReactElement => {
    const i = useLocalMessage();

    return (
        <THForm<AlertChangeStatusValuesForm>
            name="alert-change-status-form"
            onFinish={onFinish}
            form={form}
            initialValues={initialValues}
            onValuesChange={onChange}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} footerText={i(`general.confirm`)} />}
            >
                <THForm.Item
                    label={i('alert.change-status.form.status.label')}
                    name="status"
                    rules={[{ required: true, message: i('alert.change-status.form.status.required') }]}
                >
                    <THSelect>
                        {Object.entries(statusList).map(([key, values]) => (
                            <OptGroup key={key} label={key}>
                                {(values || []).map(({ value, colour }) => (
                                    <THSelect.Option key={value} value={value}>
                                        <CustomColorLabel
                                            value={i({
                                                id: `alert.data.status.${convertToLocaleId(value)}`,
                                                defaultMessage: value,
                                            })}
                                            color={colour}
                                        />
                                    </THSelect.Option>
                                ))}
                            </OptGroup>
                        ))}
                    </THSelect>
                </THForm.Item>

                <THForm.Item label={i('alert.change-status.form.summary.label')} name="summary">
                    <THMarkdown sameSize onImageUpload={onOrganisationImageUpload} />
                </THForm.Item>

                <THForm.Item label={i('alert.change-status.form.assignee.label')} name="assignee">
                    <THSelect allowClear borderless placeholder={i('alert.change-status.form.assignee.placeholder')}>
                        {assignableUsers?.map(({ _id, name, avatar, login }) => (
                            <THSelect.Option key={_id} value={login}>
                                <IconLabel key={_id} icon={<UserAvatar name={name} avatar={avatar} />} label={name} />
                            </THSelect.Option>
                        ))}
                    </THSelect>
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
